import { useState, useCallback, type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classNames from 'classnames';

import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';

import styles from './PdpSharedDescription.scss';

import { type PdpSharedDescriptionAdditionalOptionsContent_item$key } from './__generated__/PdpSharedDescriptionAdditionalOptionsContent_item.graphql.ts';
import { type PdpSharedDescriptionAdditionalOptionsContent_user$key } from './__generated__/PdpSharedDescriptionAdditionalOptionsContent_user.graphql.ts';

type TitleProps = {
    isDyoCustomizable?: boolean | null;
    isJewelry?: boolean | null;
};

export const Title: FC<TitleProps> = ({ isDyoCustomizable, isJewelry }) => {
    const defaultTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeader"
            defaultMessage="Customization"
        />
    );

    const jewelryTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.customizationHeaderJewelry"
            defaultMessage="Custom Services"
        />
    );

    const dyoTitle = (
        <FormattedMessage
            id="PdpSharedDescriptionAdditionalOptions.designYourOwnHeader"
            defaultMessage="Request Customization"
        />
    );

    if (isDyoCustomizable) {
        return dyoTitle;
    }

    return isJewelry ? jewelryTitle : defaultTitle;
};

export const designYourOwnMessage = (
    <FormattedMessage
        id="PdpSharedDescriptionAdditionalOptions.designYouOwn"
        defaultMessage="This gemstone can be customized into an extraordinary work of art under your direction. Collaborate with the jeweler to create a one-of-a-kind piece. Simply complete the form provided, and the seller will contact you."
    />
);

const userFragment = graphql`
    fragment PdpSharedDescriptionAdditionalOptionsContent_user on User {
        ...PdpSharedContactDealerButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpSharedDescriptionAdditionalOptionsContent_item on Item {
        ...PdpSharedContactDealerButton_item
        leadTimeDisplay
        isCustomizable
        seller {
            isJewelrySeller
        }
    }
`;

type Props = {
    customizationOptions?: ReactNode;
    contentClass?: string;
    contentSize?: string;
    isResizable?: boolean;
    item: PdpSharedDescriptionAdditionalOptionsContent_item$key | null;
    user: PdpSharedDescriptionAdditionalOptionsContent_user$key | null;
    isDyoCustomizable?: boolean | null;
    isFullWidthCta?: boolean;
    buttonType?: 'primary' | 'secondary' | 'textCta' | 'link' | 'transparent';
    buttonWrapperClassName?: string;
    collapsedHeight?: number;
    disableExpansion?: boolean;
    buttonSize?: 'small' | 'medium' | 'large';
};

export const PdpSharedDescriptionAdditionalOptionsContent: FC<Props> = ({
    customizationOptions,
    contentClass,
    contentSize,
    isResizable,
    item: itemRef,
    user: userRef,
    isDyoCustomizable,
    isFullWidthCta = false,
    buttonType,
    buttonWrapperClassName,
    collapsedHeight,
    disableExpansion,
    buttonSize = 'small',
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const setContentRef = useCallback((node: HTMLSpanElement | null) => {
        if (node) {
            setContentHeight(node.getBoundingClientRect().height);
        }
    }, []);

    const COLLAPSED_CONTENT_HEIGHT = collapsedHeight || 120;

    const shouldBeExpandable =
        !disableExpansion &&
        !isDyoCustomizable &&
        contentHeight !== null &&
        contentHeight >= COLLAPSED_CONTENT_HEIGHT;
    const showProductionTime =
        item?.seller?.isJewelrySeller && item?.isCustomizable && item?.leadTimeDisplay;

    const resizableContent = isResizable ? (
        <div>
            <FormattedMessage
                id="PdpSharedDescriptionAdditionalOptions.resizingAvailable"
                defaultMessage="Resizing is available."
            />
        </div>
    ) : null;

    const dyoCopy = isDyoCustomizable ? <>{designYourOwnMessage}</> : null;

    const content = (
        <span
            className={classNames(styles.content, contentClass, {
                [styles.small]: contentSize === 'small',
            })}
            ref={setContentRef}
        >
            {resizableContent}
            {customizationOptions}
            {dyoCopy}
            {showProductionTime && (
                <div>
                    <span className={styles.productionTime}>
                        <FormattedMessage
                            id="PdpSharedDescriptionAdditionalOptions.productionTime"
                            defaultMessage="Estimated Production Time"
                        />
                    </span>
                    {': '}
                    {item.leadTimeDisplay}
                </div>
            )}
        </span>
    );

    const launchOrigin = isDyoCustomizable
        ? 'dyo secondary cta'
        : 'contact link in pdp - description';

    const contactButton = (
        <div
            className={classNames({
                [styles.ctaWrapper]: buttonType !== 'link',
            })}
        >
            {item && (
                <PdpSharedContactDealerButton
                    size={buttonSize}
                    fullWidth={isFullWidthCta}
                    buttonType={buttonType || 'secondary'}
                    wrapperClassName={buttonWrapperClassName}
                    launchOrigin={launchOrigin}
                    type={'REQUEST_CUSTOMIZATION'}
                    item={item}
                    user={user}
                />
            )}
        </div>
    );
    return shouldBeExpandable ? (
        <>
            <ExpandingAreaWithReadMore
                collapsedHeight={COLLAPSED_CONTENT_HEIGHT}
                showGradient={false}
                wrapperClass={styles.expandingAreaWrapper}
            >
                {content}
            </ExpandingAreaWithReadMore>
            {contactButton}
        </>
    ) : (
        <>
            {content}
            {contactButton}
        </>
    );
};
